import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Clock, Loader2, ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Chip, Empty, Kpi, Loading, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

const STYLE = {
  pending: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  rejected: "border-border bg-muted text-muted-foreground",
};

export const SettingsChanges = () => {
  const [data, setData] = useState(null);
  const [note, setNote] = useState({});
  const [busy, setBusy] = useState("");
  const detail = useRowDetail();

  const load = useCallback(async () => {
    const res = await api.get("/settings/changes");
    setData(res.data);
  }, []);

  useEffect(() => { load().catch(() => setData(false)); }, [load]);

  const decide = async (row, approve) => {
    setBusy(row.id);
    try {
      await api.post(`/settings/changes/${row.id}/${approve ? "approve" : "reject"}`,
                     { note: note[row.id] || "" });
      toast.success(approve ? `${row.change_ref} approved and applied`
        : `${row.change_ref} declined`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return <Loading label="Loading settings changes" />;

  return (
    <div className="space-y-4" data-testid="settings-changes-panel">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi testId="kpi-settings-pending" label="Awaiting sign-off" value={data.counters.pending}
             accent="amber" icon={Clock} />
        <Kpi testId="kpi-settings-approved" label="Applied" value={data.counters.approved}
             accent="emerald" icon={CheckCircle2} />
        <Kpi testId="kpi-settings-rejected" label="Declined" value={data.counters.rejected}
             accent="slate" />
      </div>

      <Panel testId="panel-settings-changes" kicker="Board sign-off"
             title="Settings changes waiting on the Secretary or the President">
        <div className="border-b border-border px-4 py-2.5 text-[11.5px] text-muted-foreground">
          Anything beyond how the app looks is held here. The settings in force do not change until
          the Board Secretary or the President / Chairperson approves — whichever of them gets to it
          first.
        </div>
        <Table testId="settings-changes-table"
               head={<><Th>Reference</Th><Th>Area</Th><Th>What changes</Th><Th>Requested by</Th>
                 <Th>Status</Th><Th>When</Th><Th /></>}>
          {data.items.length === 0 && <Empty colSpan={7} label="No settings changes to show" />}
          {data.items.map((c) => (
            <tr key={c.id} className="row-hover cursor-pointer"
                data-testid={`settings-change-row-${c.change_ref}`}
                onClick={rowClick(() => detail.show(c, c.change_ref, c.change_ref, c.area_label))}>
              <Td className="font-mono text-[11px]">{c.change_ref}</Td>
              <Td className="text-[12px]">{c.area_label}</Td>
              <Td className="max-w-[280px]">
                <div className="text-[11.5px] text-foreground">{c.summary}</div>
                <div className="truncate font-mono text-[10px] text-muted-foreground">
                  {(c.changes || []).slice(0, 3)
                    .map((ch) => `${ch.field}: ${ch.from} → ${ch.to}`).join(" · ")}
                </div>
              </Td>
              <Td className="text-[11.5px]">{c.requested_by}</Td>
              <Td><Chip className={STYLE[c.status]}>{c.status}</Chip></Td>
              <Td className="font-mono text-[10.5px]">{dateTime(c.created_at)}</Td>
              <Td>
                {c.status === "pending" && data.may_decide && (
                  <div className="flex items-center justify-end gap-1.5">
                    <Input value={note[c.id] || ""} placeholder="Note"
                           data-testid={`settings-change-note-${c.change_ref}`}
                           onChange={(e) => setNote({ ...note, [c.id]: e.target.value })}
                           className="h-7 w-28 bg-muted/40 text-[11px]" />
                    <Button size="sm" variant="outline" disabled={busy === c.id}
                            onClick={() => decide(c, true)}
                            data-testid={`settings-change-approve-${c.change_ref}`}
                            className="h-7 rounded-full border-border px-2.5 text-[10px]">
                      {busy === c.id ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <ThumbsUp className="h-3 w-3" />}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={busy === c.id}
                            onClick={() => decide(c, false)}
                            data-testid={`settings-change-reject-${c.change_ref}`}
                            className="h-7 rounded-full px-2.5 text-[10px] text-muted-foreground">
                      <ThumbsDown className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>
      {detail.sheet}
    </div>
  );
};
